import { useDebounceFn } from '@vueuse/core';
import type { Coordinate } from 'ol/coordinate';
import type { Ref } from 'vue';
import { onBeforeUnmount, onMounted, ref } from 'vue';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';

export interface UseMapCoordinatesOptions {
  map: NoraMap;
}

export interface UseMapCoordinatesReturn {
  centerCoordinates: Ref<Coordinate | null>;
}

export function useMapCoordinates(options: UseMapCoordinatesOptions): UseMapCoordinatesReturn {
  const { map } = options;
  const centerCoordinates = ref<Coordinate | null>(null);

  let resizeObserver: ResizeObserver | null = null;

  const updateCoordinates = useDebounceFn(
    () => {
      const olMap = map.getOlMap();

      if (!olMap) {
        return;
      }

      const center = olMap.getView()?.getCenter();
      centerCoordinates.value = center ?? null;
    },
    50,
    { maxWait: 200 },
  );

  async function setup(): Promise<void> {
    const olMap = map.getOlMap();

    if (!olMap) {
      return;
    }

    const view = olMap.getView();

    // Event listeners
    olMap.on('moveend', updateCoordinates);
    view.on(['change:resolution', 'change:center'], updateCoordinates);

    // Resize observer
    const targetEl = olMap.getTargetElement();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        void updateCoordinates();
      });
      resizeObserver.observe(targetEl);
    }

    await updateCoordinates();
  }

  function cleanup(): void {
    const olMap = map.getOlMap();

    if (!olMap) {
      return;
    }

    const view = olMap.getView();

    olMap.un('moveend', updateCoordinates);
    view.un(['change:resolution', 'change:center'], updateCoordinates);

    resizeObserver?.disconnect();
    resizeObserver = null;
  }

  onMounted(setup);
  onBeforeUnmount(cleanup);

  return {
    centerCoordinates,
  };
}
